import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ThemeToggle } from "../components/ThemeToggle";
import { BrandLogo } from "../components/BrandLogo";
import { api, formatApiErrorDetail } from "../lib/api";

const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-6 relative">
    <ThemeToggle className="absolute top-5 right-5" />
    <div className="w-full max-w-sm space-y-6 fade-up">
      <BrandLogo />
      {children}
    </div>
  </div>
);

const ForgotForm = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return <p data-testid="forgot-sent" className="text-sm text-[#10B981]">Se o e-mail estiver cadastrado, você vai receber um link para criar uma nova senha. Ele vale por 1 hora.</p>;
  }
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
      <div className="space-y-1.5">
        <Label>E-mail cadastrado</Label>
        <Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#181B21] border-[#2D333F] h-11" />
      </div>
      {error && <p data-testid="forgot-error" className="text-sm text-red-400">{error}</p>}
      <Button data-testid="forgot-submit-btn" disabled={busy} className="w-full h-11 bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enviar link
      </Button>
    </form>
  );
};

const ResetForm = ({ token }) => {
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) { setError("As senhas não conferem."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw.a });
      toast.success("Senha alterada! Entre com a nova senha.");
      nav("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
      {[["a", "Nova senha"], ["b", "Repita a nova senha"]].map(([k, l]) => (
        <div key={k} className="space-y-1.5">
          <Label>{l}</Label>
          <Input data-testid={`reset-password-${k}`} type="password" required minLength={6} value={pw[k]} onChange={(e) => setPw({ ...pw, [k]: e.target.value })} className="bg-[#181B21] border-[#2D333F] h-11" />
        </div>
      ))}
      {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
      <Button data-testid="reset-submit-btn" disabled={busy} className="w-full h-11 bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salvar nova senha
      </Button>
    </form>
  );
};

export default function PasswordRecovery() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return (
    <Shell>
      <div>
        <h2 className="font-heading text-3xl font-bold tracking-tight">{token ? "Nova senha" : "Esqueci minha senha"}</h2>
        <p className="text-sm text-[#9CA3AF] mt-1">{token ? "Crie uma nova senha de pelo menos 6 caracteres." : "Informe seu e-mail e enviaremos um link para criar uma nova senha."}</p>
      </div>
      {token ? <ResetForm token={token} /> : <ForgotForm />}
      <Link data-testid="recovery-back-link" to="/login" className="block text-sm text-center text-[#E67E22] hover:underline">Voltar para o login</Link>
    </Shell>
  );
}
